<script lang="ts">
    import { App, PRODUCT_VARIANT_DEFAULT_TITLE } from "@/constants";
    import { WISHLIST_WIDGET_NAMES } from "@/constants/widget-names";
    import { urlsBuilderContextToken } from "@/context_tokens";
    import { modalManagerToken } from "@/tokens";
    import { AddToWishlistButtonWidgetDefinition } from "@apps/wishlist/definitions";
    import { wishlistApplicationLoggerToken } from "@apps/wishlist/tokens";
    import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
    import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
    import { ProductVariantIdFromStaticProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromStaticProvider";
    import { ProductVariantsIdManager } from "@lib/ProductVariantIdManager/ProductVariantIdManager";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { BaseModule } from "@modules/base_module/BaseModule";
    import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { ExtractModuleType } from "@modules/dynamic_import/types";
    import type ProductEntity from "@modules/products/entities/ProductEntity";
    import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
    import { dispatchSdkEvent } from "@modules/sdk/helpers";
    import { container } from "tsyringe";

    import Icon from "@components/Icon.svelte";
    import ArrowTopRightOnSquare from "@components/icons/ArrowTopRightOnSquare.svelte";
    import Image from "@components/Image/Image.svelte";
    import Money from "@components/Money.svelte";
    import Widget from "@components/Widget.svelte";

    export let product: ProductEntity;
    export let productUrl: string | null;
    export let dynamicImportManager: DynamicImportManager;

    const urlsBuilder = getTypedContext(urlsBuilderContextToken);

    const modulesStatusesProvider =
        container.resolve(BaseModule).exports.modulesStatusesProvider;

    $: productVariant = product.variants[0];

    const productIdManager = new ProductIdManager();
    let productIdFromStaticProvider: ProductIdFromStaticProvider;

    const updateProductId = (productId: number) => {
        if (productIdFromStaticProvider) {
            productIdManager.unregisterProvider(productIdFromStaticProvider);
        }
        productIdFromStaticProvider = new ProductIdFromStaticProvider(
            productId
        );
        productIdManager.registerProvider(productIdFromStaticProvider);
    };
    $: {
        updateProductId(product.id);
    }

    const productVariantsIdManager = new ProductVariantsIdManager();
    let productVariantIdFromStaticProvider: ProductVariantIdFromStaticProvider;

    const updateProductVariantId = (variantId: number) => {
        if (productVariantIdFromStaticProvider) {
            productVariantsIdManager.unregisterProvider(
                productVariantIdFromStaticProvider
            );
        }
        productVariantIdFromStaticProvider =
            new ProductVariantIdFromStaticProvider(variantId);
        productVariantsIdManager.registerProvider(
            productVariantIdFromStaticProvider
        );
    };

    $: {
        updateProductVariantId(productVariant.id);
    }

    const wishlistAppStatus$ = modulesStatusesProvider.selectModuleStatus(
        App.Wishlist
    );

    let AddToWishlistButtonWidget:
        | ExtractModuleType<
              typeof AddToWishlistButtonWidgetDefinition
          >["default"]
        | null = null;

    const importAddToWishlistButtonWidget = async () => {
        if (AddToWishlistButtonWidget) return;

        try {
            AddToWishlistButtonWidget =
                await dynamicImportManager.importDefault(
                    AddToWishlistButtonWidgetDefinition,
                    true
                );
        } catch (error) {
            console.error(
                "Failed to load AddToWishlistButtonWidget module",
                error
            );
        }
    };

    $: if ($wishlistAppStatus$ === MODULE_STATUS.INITIALIZED) {
        importAddToWishlistButtonWidget();
    }

    const handleAddToWishlistButtonWidgetInit = () => {
        dispatchSdkEvent(
            new WidgetInjectedSdkEvent(
                WISHLIST_WIDGET_NAMES.ADD_TO_WISHLIST_BUTTON_WIDGET
            )
        );
    };

    $: firstVariantOfProduct = product.variants[0];
</script>

<div class="gw-rv-product">
    <div class="gw-rv-product__inner">
        <div class="gw-rv-product__info">
            <div class="gw-rv-product__left">
                <a
                    class="gw-rv-product__image-link"
                    href={productUrl}
                    aria-label={product.title}
                >
                    <Image
                        src={urlsBuilder.createProductImageUrl(
                            product.featuredImage?.url || "",
                            { width: 128 }
                        )}
                        alt={firstVariantOfProduct.image?.altText || ""}
                        blur
                        imgClassName="gw-rv-product__image"
                        objectFit="contain"
                    />
                </a>
            </div>
            <div class="gw-rv-product__center">
                <a
                    class="gw-rv-product__title"
                    href={productUrl}
                    target="_blank"
                >
                    <h4 class="gw-rv-product__title-text">
                        {product.title}
                    </h4>
                    <div class="gw-rv-product__title-link-icon">
                        <Icon color="currentColor" width={12} height={12}>
                            <ArrowTopRightOnSquare />
                        </Icon>
                    </div>
                </a>
                {#if firstVariantOfProduct.title !== PRODUCT_VARIANT_DEFAULT_TITLE}
                    <h5 class="gw-rv-product__sub-title">
                        {firstVariantOfProduct.title}
                    </h5>
                {/if}

                <span class="gw-rv-product__price">
                    <Money amount={firstVariantOfProduct.price.amount} />
                </span>
            </div>

            <div class="gw-rv-product__right">
                {#if $wishlistAppStatus$ === MODULE_STATUS.INITIALIZED && AddToWishlistButtonWidget}
                    <Widget
                        class="gw-rv-product__add-to-wishlist-widget-cointainer"
                        widget={AddToWishlistButtonWidget}
                        params={[
                            {},
                            container.resolve(wishlistApplicationLoggerToken),
                            container.resolve(modalManagerToken),
                            productIdManager,
                            productVariantsIdManager,
                            dynamicImportManager,
                        ]}
                        on:widgetInit={handleAddToWishlistButtonWidgetInit}
                    />
                {/if}
            </div>
        </div>
    </div>
</div>

<style lang="postcss">
    .gw-rv-product {
        background-color: inherit;

        &__inner {
            border-top: 1px solid #f0f0f0;
            padding: 1.25em 0;
            display: flex;
            justify-content: space-between;

            @media all and (max-width: 600px) {
                padding: 1em 0;
            }
        }

        &__info {
            width: 100%;
            display: flex;
            justify-content: space-between;
            gap: 0.75em;
            align-items: center;
            flex-wrap: wrap;
        }

        &__left {
            width: 60px;
            height: 60px;
        }

        &__center {
            flex: 1;
        }

        &__right {
        }

        &__title {
            color: inherit;
            text-decoration: none;
            display: flex;
            column-gap: 4px;
            align-items: center;

            &:hover {
                text-decoration: underline;
            }

            &-text {
                margin: 0;
                font-size: 0.875em;
                line-height: normal;
                font-weight: 600;
                display: -webkit-box;
                -webkit-line-clamp: 1;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }

            &-link-icon {
                visibility: hidden;
                line-height: 1;
            }

            &:hover &-link-icon {
                visibility: visible;
            }
        }

        &__sub-title {
            margin: 2px 0 0;
            font-size: 0.75em;
            line-height: normal;
            font-weight: 400;
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        &__price {
            margin-top: 0.625em;
            display: inline-block;
            font-size: 0.75em;
        }

        &__image-link {
        }
    }

    :global(.gw-rv-product__image) {
        border-radius: 3px;
        box-sizing: border-box;
        box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
    }
</style>
